<template>
  <Modal
    :title="title"
    size="400"
    :submitBtnText="saving ? 'Saving...' : 'Save Changes'"
    :disabled="saving"
    @hide-modal="handleClose"
    @submit="handleSubmit"
  >
    <div class="section-spacing-15">
      <div v-if="timeCard?.employee_id" class="employee-details">
        <div class="text-title">Employee:</div>
        <a v-if="employee?.links?.show" :href="employee?.links?.show">{{
          employee?.full_name
        }}</a>
        <div v-else>{{ employee?.full_name }}</div>
      </div>
      <div v-else>
        <Select
          v-model="employeeId"
          label="Employee"
          :options="employeeOptions"
          search
          :field-error="firstErrorMessage(v$.employeeId)"
        />
      </div>
      <div class="d-flex gap-3 align-items-center">
        <TimePicker
          v-model="startTime"
          class="mb-0"
          :format="timeFormat"
          :input-format="timeFormat"
          placeholder="--"
          label="Start Time"
          :field-error="firstErrorMessage(v$.startTime)"
          data-cy="startTime"
        />
        <div class="text-title mt-4">On</div>
        <Datepicker
          v-model="startDate"
          class="mb-0"
          label="Date"
          :field-error-message="firstErrorMessage(v$.startDate)"
          data-cy="startDate"
        />
      </div>
      <div class="d-flex gap-3 align-items-center">
        <TimePicker
          v-model="endTime"
          class="mb-0"
          :format="timeFormat"
          :input-format="timeFormat"
          placeholder="--"
          label="End Time"
          :field-error="firstErrorMessage(v$.endTime)"
          data-cy="endTime"
        />
        <div class="text-title mt-4">On</div>
        <Datepicker
          v-model="endDate"
          label="Date"
          class="mb-0"
          :field-error-message="firstErrorMessage(v$.endDate)"
          data-cy="endDate"
        />
      </div>
      <sera-input
        v-if="hasChanges && timeCard?.id"
        :value="reason"
        multiline
        label="Notes:"
        :errorMessage="firstErrorMessage(v$.reason)"
        data-cy="reason"
        @change="({ detail }) => (reason = detail)"
      ></sera-input>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import debounce from "lodash/debounce";
import moment from "moment";
import { computed, onBeforeMount, ref, watch } from "vue";
import { EventBus } from "@/lib/event_bus";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";
import { useStore } from "vuex";

import Datepicker from "@/components/Datepicker.vue";
import Modal from "@/components/Modal.vue";
import TimePicker from "@/components/TimePicker.vue";
import { customDateFormatWithTimezone, getCombinedDateTime } from "@/mixins";
import { TimeCard } from "@/typings/timeCard";
import useVuelidate from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";

const emits = defineEmits(["close"]);
const store = useStore();
const props = defineProps<{ timeCard?: TimeCard }>();
const startDate = ref<Date | string>(new Date());
const endDate = ref<Date | string>("");
const startTime = ref<Date | string | null>(null);
const endTime = ref<Date | string | null>(null);
const employeeId = ref<number | null>(null);
const reason = ref(null);
const saving = ref(false);
const timeFormat = ref("hh:mm a");

const title = computed(() => {
  if (props.timeCard) {
    return `Edit Time Card ID - ${props.timeCard.id}`;
  }
  return "Create Time Card";
});
const employee = computed(() => {
  if (!store.state.timeCardEmployees.length) {
    return { full_name: "--" };
  }
  return store.state.timeCardEmployees.find(
    (employee: { id: number }) => employee.id === props.timeCard?.employee_id
  );
});
const hasChanges = computed(() => {
  if (!props.timeCard) {
    return false;
  }
  const originalStart = getCombinedDateTime(
    props.timeCard.start_time,
    props.timeCard.start_time
  ).toISOString();
  const newStart = getCombinedDateTime(
    startDate.value,
    startTime.value
  ).toISOString();
  const originalEnd = getCombinedDateTime(
    props.timeCard.end_time,
    props.timeCard.end_time
  ).toISOString();
  const newEnd = getCombinedDateTime(
    endDate.value,
    endTime.value
  ).toISOString();
  return originalStart !== newStart || originalEnd !== newEnd;
});
const employeeOptions = computed(() =>
  store.state.timeCardEmployees.map(
    (employee: { full_name: string; role: string; gid: string }) => ({
      label: employee.full_name + ` (${employee.role})`,
      value: employee.gid
    })
  )
);

const startIsBeforeEnd = () => {
  if (
    !startDate.value ||
    !startTime.value ||
    !endDate.value ||
    !endTime.value
  ) {
    return true;
  }
  const start = getCombinedDateTime(startDate.value, startTime.value);
  const end = getCombinedDateTime(endDate.value, endTime.value);
  return start.diff(end, "minutes") < 0;
};

const startIsFuture = () => {
  if (!startDate.value || !startTime.value) {
    return true;
  }
  const start = getCombinedDateTime(startDate.value, startTime.value);
  return moment().isAfter(start);
};

const v$ = useVuelidate(
  {
    employeeId: {
      required: helpers.withMessage("Employee is required", required)
    },
    startDate: {
      required: helpers.withMessage("Date is required", required),
      beforeEnd: helpers.withMessage("Must be before end", startIsBeforeEnd),
      isFuture: helpers.withMessage("Start can't be in future", startIsFuture)
    },
    startTime: {
      required: helpers.withMessage("Start Time is required", required),
      beforeEnd: helpers.withMessage("Must be before end", startIsBeforeEnd),
      isFuture: helpers.withMessage("Start can't be in future", startIsFuture)
    },
    endTime: {
      requiredIf: helpers.withMessage(
        "End Time is required",
        requiredIf(() => !!endDate.value)
      ),
      afterStart: helpers.withMessage("Must be after start", startIsBeforeEnd)
    },
    endDate: {
      requiredIf: helpers.withMessage(
        "End Date is required",
        requiredIf(() => !!endTime.value)
      ),
      afterStart: helpers.withMessage("Must be after start", startIsBeforeEnd)
    },
    reason: {
      reason: helpers.withMessage(
        "Notes are required",
        requiredIf(() => !!hasChanges.value)
      )
    }
  },
  {
    employeeId,
    startDate,
    startTime,
    endDate,
    endTime,
    reason
  }
);

onBeforeMount(() => {
  if (props.timeCard) {
    setFormData();
  }
  if (!store.state.timeCardEmployees.length) {
    store.dispatch("fetchTimeCardEmployees");
  }
});

async function setFormData() {
  if (props.timeCard) {
    startDate.value = props.timeCard.start_time;
    startTime.value = customDateFormatWithTimezone(
      props.timeCard.start_time,
      timeFormat.value
    );
    endDate.value = props.timeCard.end_time;
    endTime.value = customDateFormatWithTimezone(
      props.timeCard.end_time,
      timeFormat.value
    );
    employeeId.value = props.timeCard.employee_id || null;
  }
}

const save = async () => {
  saving.value = true;
  try {
    const valid = await validate();
    if (!valid) throw "Invalid form";
    const action = props.timeCard ? "updateTimeCard" : "createTimeCard";

    await store.dispatch(action, {
      ...props.timeCard,
      start_time: getCombinedDateTime(
        startDate.value,
        startTime.value
      ).toISOString(),
      end_time: getCombinedDateTime(endDate.value, endTime.value).toISOString(),
      employee_gid: employeeId.value,
      reason: reason.value
    });

    window.notify.success("Time Card saved successfully!");

    handleClose();
  } catch (e) {
    console.error(e);
  }
  saving.value = false;
};

const handleSubmit = debounce(
  async () => {
    await save();
  },
  250,
  { immediate: true }
);

function handleClose() {
  EventBus.$emit("refresh-datatable");
  emits("close");
}

async function validate() {
  return v$.value.$validate();
}

watch(startDate, (newVal) => {
  if (!newVal) {
    startTime.value = null;
  }
});

watch(endDate, (newVal) => {
  if (!newVal) {
    endTime.value = null;
  }
});
</script>

<style scoped>
.employee-details {
  background: var(--sera-color-neutral-gray-light-gray-50);
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 5px;
}
</style>
